export type ScoringCategoryId = "social" | "backers" | "pedigree" | "momentum" | "dev" | "revenue";

export interface ScoringCategoryDefinition {
  id: ScoringCategoryId;
  label: string;
  weight: number;
  description: string;
  providerSummary: string;
  ruleSummary: string;
}

export const SCORING_CONTRACT_VERSION = "v5.2";

export const SCORING_CATEGORIES = [
  {
    id: "social",
    label: "Social",
    weight: 20,
    description: "Aggregated social presence score",
    providerSummary: "Sorsa, Moni, TwitterScore, optional Community multiplier",
    ruleSummary:
      "Average Sorsa, Moni, and TwitterScore sub-scores with outlier dampening, then apply the optional community multiplier.",
  },
  {
    id: "backers",
    label: "Backers",
    weight: 25,
    description: "Investor quality and fundraising traction",
    providerSummary: "Raises provider",
    ruleSummary: "Use the normalized Raises provider sub-score directly.",
  },
  {
    id: "pedigree",
    label: "Pedigree",
    weight: 20,
    description: "Founder track record and credentials",
    providerSummary: "Pedigree provider",
    ruleSummary: "Use the normalized Pedigree provider sub-score directly.",
  },
  {
    id: "momentum",
    label: "Momentum",
    weight: 25,
    description: "Recent growth and activity velocity",
    providerSummary: "Momentum provider",
    ruleSummary: "Multiply the Momentum provider sub-score by the category weight.",
  },
  {
    id: "dev",
    label: "Dev",
    weight: 5,
    description: "GitHub development activity",
    providerSummary: "GitHub provider",
    ruleSummary: "Use the normalized GitHub provider sub-score directly.",
  },
  {
    id: "revenue",
    label: "Revenue",
    weight: 5,
    description: "Protocol revenue via DefiLlama",
    providerSummary: "DefiLlama provider",
    ruleSummary:
      "Award the full category weight only when the DefiLlama revenue gate passes; otherwise award zero.",
  },
] as const satisfies readonly ScoringCategoryDefinition[];

function buildCategoryWeights() {
  const weights = {} as Record<ScoringCategoryId, number>;
  for (const category of SCORING_CATEGORIES) {
    weights[category.id] = category.weight;
  }
  return Object.freeze(weights);
}

function buildCategoryTooltips() {
  const tooltips = {} as Record<string, string>;
  for (const category of SCORING_CATEGORIES) {
    tooltips[category.label] = `${category.description} (max ${category.weight} pts)`;
  }
  return Object.freeze(tooltips);
}

export const CATEGORY_WEIGHTS = buildCategoryWeights();
export const CATEGORY_TOOLTIPS = buildCategoryTooltips();
export const MAX_ALPHA_SCORE = SCORING_CATEGORIES.reduce(
  (total, category) => total + category.weight,
  0
);

export const SCORING_CATEGORY_IDS = SCORING_CATEGORIES.map((category) => category.id);
